import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FORM_FIELD_TYPES } from '../config';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { useToast } from './Toast';
import { Upload, X, CheckCircle, AlertCircle, Camera, Plus, Trash2 } from 'lucide-react';
import FaceCapture from './FaceCapture';
import CouponApplyField from './CouponApplyField';
import { applyDiscount } from '../utils/couponMath';
import { addPricedOption, chargeable, optionCurrency, optionPrice } from '../utils/pricing';
import { formInitialValues, toFieldName } from '../utils/formInitialValues';
import { useDocumentLimit } from '../utils/documentLimit';

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const getMatchingOption = (field, selectedValue) => {
  if (!field?.optionsWithPricing || selectedValue == null) return null;
  const normalizedSelected = normalizeText(selectedValue);
  return field.optionsWithPricing.find(
    (option) => normalizeText(option?.label) === normalizedSelected
  ) || null;
};

const getVisibleFields = (fields = [], values = {}, prefilledValues = {}) => {
  const visible = [];

  fields.forEach((field) => {
    if (!field) return;
    visible.push(field);

    if (field.type !== 'dropdown' && field.type !== 'radio') return;
    const selectedValue = values[toFieldName(field.label)] ?? prefilledValues[field.label];
    const selectedOption = getMatchingOption(field, selectedValue);

    if (selectedOption?.subFields?.length) {
      visible.push(...getVisibleFields(selectedOption.subFields, values, prefilledValues));
    }
  });

  return visible;
};

const calculatePriceTotals = (fields = [], values = {}, prefilledValues = {}, listValues = {}, totals = {}) => {
  fields.forEach((field) => {
    if (field?.type === 'list' && field.price > 0) {
      const items = listValues[toFieldName(field.label)] || [];
      const filledCount = items.filter(item => item && item.trim() !== '').length;
      if (filledCount) {
        const currency = optionCurrency(field);
        totals[currency] = (totals[currency] || 0) + field.price * filledCount;
      }
      return;
    }

    if (!field?.optionsWithPricing?.length) return;

    const selectedValue = values[toFieldName(field.label)] ?? prefilledValues[field.label];
    const selectedOption = getMatchingOption(field, selectedValue);
    if (!selectedOption) return;

    Object.assign(totals, addPricedOption(totals, selectedOption));

    if (selectedOption?.subFields?.length) {
      calculatePriceTotals(selectedOption.subFields, values, prefilledValues, listValues, totals);
    }
  });

  return totals;
};

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readDraft = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    return saved && Date.now() - saved.savedAt <= DRAFT_TTL_MS ? saved.values || {} : {};
  } catch (_) {
    return {};
  }
};

const isPlainDraftValue = (value) => ['string', 'number', 'boolean'].includes(typeof value)
  || (Array.isArray(value) && value.every((item) => typeof item === 'string'));

const DynamicForm = ({ 
  formSchema = [], 
  onSubmit, 
  submitLabel = "Submit", 
  isLoading = false,
  className = "",
  showPricing = false,
  eventId = null,
  eventData = null,
  hasFormBackground = false,
  renderPayment = null,
  validatePayment = null,
  initialValues = null,
  // Local draft of typed answers, restored on return for DRAFT_TTL_MS; files are not kept.
  draftKey = null,
  // Discount from an invite access code: { discountType, discountValue, maxDiscountAmount, label }; replaces coupons.
  priceRule = null
}) => {
  const documentLimit = useDocumentLimit();
  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const prefilledValues = Object.fromEntries(urlParams.entries());
  // Initialize form with prefilled values
  const initial = initialValues ? formInitialValues(formSchema, initialValues) : null;
  const draftValues = draftKey ? readDraft(draftKey) : {};
  const defaultValues = { ...draftValues, ...(initial?.values || {}) };
  const allPossibleFields = getVisibleFields(formSchema, {}, prefilledValues);
  allPossibleFields.forEach((field) => {
    const fieldName = toFieldName(field.label);
    if (prefilledValues[field.label]) {
      defaultValues[fieldName] = prefilledValues[field.label];
    }
  });

  const { register, handleSubmit, control, formState: { errors }, setValue, watch } = useForm({
    defaultValues
  });
  const [filePreviews, setFilePreviews] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedUserId, setUploadedUserId] = useState(null);
  const [faceImage, setFaceImage] = useState(null);
  const [faceError, setFaceError] = useState(null);
  const [showFaceCapture, setShowFaceCapture] = useState(false);
  const [listValues, setListValues] = useState(() => initial?.lists || {});
  const [fileErrors, setFileErrors] = useState({});
  const [couponQuote, setCouponQuote] = useState(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (!draftKey) return undefined;
    const subscription = watch((values) => {
      try {
        const plain = Object.fromEntries(Object.entries(values).filter(([, value]) => isPlainDraftValue(value)));
        localStorage.setItem(draftKey, JSON.stringify({ savedAt: Date.now(), values: plain }));
      } catch (_) { /* storage unavailable */ }
    });
    return () => subscription.unsubscribe();
  }, [draftKey, watch]);
  
  // Watch all form values to calculate total price
  const watchedValues = watch();


  const handleFileChange = async (fieldName, event) => {
    const file = event.target.files[0];
    if (file && file.size > documentLimit.bytes) {
      setFileErrors((prev) => ({ ...prev, [fieldName]: `Files can be at most ${documentLimit.mb} MB` }));
      event.target.value = '';
      return;
    }
    if (file && eventId) {
      setValue(fieldName, file);
      setUploadingFiles(prev => ({ ...prev, [fieldName]: true }));
      
      try {
        // Upload file immediately
        const uploadResult = await ApiService.uploadFile(file, eventId, fieldName.toUpperCase());
        
        // Store the document ID for this field
        setUploadedDocuments(prev => ({
          ...prev,
          [fieldName]: uploadResult.documentId || uploadResult.externalId
        }));

        setFileErrors(prev => {
          if (!prev[fieldName]) return prev;
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
        
        // Store the userId from upload response (if provided)
        if (uploadResult.userId) {
          setUploadedUserId(uploadResult.userId);
        }
        
        showSuccess(`${file.name} uploaded successfully!`);
        
        // Create preview for images
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFilePreviews(prev => ({
              ...prev,
              [fieldName]: e.target.result
            }));
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('File upload error:', error);
        showError(`Failed to upload ${file.name}. Please try again.`);
        // Clear the file input on error
        event.target.value = '';
        setValue(fieldName, null);
      } finally {
        setUploadingFiles(prev => ({ ...prev, [fieldName]: false }));
      }
    }
  };

  const removeUploadedFile = (fieldName) => {
    setUploadedDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[fieldName];
      return newDocs;
    });
    setFilePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[fieldName];
      return newPreviews;
    });
    setValue(fieldName, null);
  };

  const renderField = (field, index) => {
    const fieldType = FORM_FIELD_TYPES[field.type];
    if (!fieldType) return null;

    const fieldName = toFieldName(field.label);
    const isRequired = field.required;
    const error = errors[fieldName];
    // Use a stable, label-derived key so fields preserve their React identity
    // when sub-fields conditionally appear/disappear and indices shift.
    const fieldKey = fieldName || `field-${index}`;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'date':
      case 'number': {
        const hasPlaceholderImage = !!field.placeholderImageUrl;
        const fieldValue = watch(fieldName);
        const showPlaceholderImage = hasPlaceholderImage && !fieldValue && !prefilledValues[field.label];
        const fieldBg = hasFormBackground ? 'bg-white/70' : 'bg-white/50';
        return (
          <div key={fieldKey} className={`relative ${fieldBg} backdrop-blur-sm rounded-lg p-4 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100`}>
            <Label htmlFor={fieldName} className="text-gray-700 font-medium flex items-center text-sm mb-2">
              <span>{field.label}</span>
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="relative">
              <Input
                id={fieldName}
                type={fieldType.inputType}
                placeholder={!hasPlaceholderImage ? field.placeholder : ''}
                {...register(fieldName, { 
                  required: isRequired,
                  ...(field.type === 'email' && { pattern: /^\S+@\S+$/i })
                })}
                className={`h-10 px-3 text-base bg-white ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'} ${!!prefilledValues[field.label] ? 'bg-gray-50' : ''} rounded-lg transition-all duration-200`}
                disabled={!!prefilledValues[field.label]}
              />
              {showPlaceholderImage && (
                <div className="absolute inset-0 flex items-center px-3 pointer-events-none rounded-lg overflow-hidden">
                  <img src={field.placeholderImageUrl} alt="" className="max-h-8 max-w-full object-contain" />
                </div>
              )}
              {!!prefilledValues[field.label] && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                    Pre-filled
                  </span>
                </div>
              )}
            </div>
            {error && (
              <div className="mt-1.5 flex items-center text-red-500 text-xs">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                <p>
                  {error.type === 'required' ? 'Required' : 'Invalid format'}
                </p>
              </div>
            )}
          </div>
        );
      }

      case 'textarea': {
        const textareaPlaceholderImage = !!field.placeholderImageUrl;
        const textareaValue = watch(fieldName);
        const showTextareaPlaceholderImage = textareaPlaceholderImage && !textareaValue && !prefilledValues[field.label];
        const textareaFieldBg = hasFormBackground ? 'bg-white/70' : 'bg-white/50';
        return (
          <div key={fieldKey} className={`relative ${textareaFieldBg} backdrop-blur-sm rounded-lg p-4 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 md:col-span-2`}>
            <Label htmlFor={fieldName} className="text-gray-700 font-medium flex items-center text-sm mb-2">
              <span>{field.label}</span>
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="relative">
              <Textarea
                id={fieldName}
                placeholder={!textareaPlaceholderImage ? field.placeholder : ''}
                {...register(fieldName, { required: isRequired })}
                className={`min-h-[80px] text-base bg-white rounded-lg ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'} transition-all duration-200`}
              />
              {showTextareaPlaceholderImage && (
                <div className="absolute inset-0 flex items-center justify-center p-3 pointer-events-none rounded-lg overflow-hidden">
                  <img src={field.placeholderImageUrl} alt="" className="max-h-16 max-w-full object-contain" />
                </div>
              )}
            </div>
            {error && (
              <div className="mt-1.5 flex items-center text-red-500 text-xs">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                <p>Required</p>
              </div>
            )}
          </div>
        );
      }

      case 'dropdown': {
        const optionsWithPricing =
          field.optionsWithPricing ||
          field.options?.map(opt => ({ label: opt, price: 0, slots: null, availableSlots: null })) ||
          [];
        const hasPricing = optionsWithPricing.some(opt => optionPrice(opt) > 0);
        const dropdownFieldBg = hasFormBackground ? 'bg-white/70' : 'bg-white/50';
        return (
          <div key={fieldKey} className={`relative ${dropdownFieldBg} backdrop-blur-sm rounded-lg p-4 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100`}>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor={fieldName} className="text-gray-700 font-medium flex items-center text-sm">
                <span>{field.label}</span>
                {isRequired && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {hasPricing && showPricing && (
                <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center">
                  <span className="mr-0.5">💰</span> Varies
                </span>
              )}
            </div>
            <div className="relative">
              <Controller
                name={fieldName}
                control={control}
                rules={{ required: isRequired ? 'Required' : false }}
                render={({ field: ctrlField }) => {
                  const dropdownValue = prefilledValues[field.label] ?? ctrlField.value;
                  const selectedOptionMeta = optionsWithPricing.find(opt => opt.label === dropdownValue);
                  const selectedHasLimitedSlots =
                    selectedOptionMeta && Number.isInteger(selectedOptionMeta.slots) && selectedOptionMeta.slots > 0;
                  const selectedAvailableSlots = selectedHasLimitedSlots
                    ? (selectedOptionMeta.availableSlots ?? selectedOptionMeta.slots)
                    : null;
                  const selectedIsSoldOut = selectedHasLimitedSlots && selectedAvailableSlots <= 0;
                  const showDropdownPlaceholderImage =
                    !!field.placeholderImageUrl && !dropdownValue && !prefilledValues[field.label];

                  return (
                    <>
                      <Select
                        onValueChange={(value) => {
                          ctrlField.onChange(value);
                        }}
                        disabled={!!prefilledValues[field.label]}
                        value={dropdownValue ?? undefined}
                      >
                        <SelectTrigger
                          id={fieldName}
                          className={`h-10 text-base bg-white rounded-lg ${error ? 'border-red-500' : 'border-gray-200'} transition-all duration-200 relative`}
                        >
                          <SelectValue placeholder={showDropdownPlaceholderImage ? '' : `Select ${field.label}`} />
                          {showDropdownPlaceholderImage && (
                            <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
                              <img src={field.placeholderImageUrl} alt="" className="max-h-6 max-w-[140px] object-contain" />
                            </div>
                          )}
                        </SelectTrigger>
                        <SelectContent className="bg-white/95 backdrop-blur-sm border-gray-200 shadow-lg">
                          {(() => {
                            const prefilledValue = prefilledValues[field.label];
                            const existingOptions = optionsWithPricing
                              .filter(option => option.label && option.label.trim() !== '')
                              .map(option => option.label);

                            const allOptions = prefilledValue && !existingOptions.includes(prefilledValue)
                              ? [{ label: prefilledValue, price: 0, slots: null, availableSlots: null, isCustomValue: true }, ...optionsWithPricing]
                              : optionsWithPricing;

                            return allOptions
                              .filter(option => {
                                if (prefilledValue) {
                                  return option.label === prefilledValue;
                                }
                                return option.label && option.label.trim() !== '';
                              })
                              .map((option, optIndex) => {
                                const hasLimitedSlots = Number.isInteger(option.slots) && option.slots > 0;
                                const availableSlots = hasLimitedSlots
                                  ? (option.availableSlots ?? option.slots)
                                  : null;
                                const isSoldOut = hasLimitedSlots && availableSlots <= 0;
                                const isSelected = dropdownValue === option.label;

                                return (
                                  <SelectItem
                                    key={optIndex}
                                    value={option.label}
                                    disabled={isSoldOut && !isSelected}
                                    className="focus:bg-blue-50 focus:text-blue-700"
                                  >
                                    <span className="font-medium">
                                      {option.label}
                                      {optionPrice(option) > 0 ? ` — ${formatCurrency(optionPrice(option), optionCurrency(option))}` : ''}
                                    </span>
                                  </SelectItem>
                                );
                              });
                          })()}
                        </SelectContent>
                      </Select>
                      {!!selectedOptionMeta && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {selectedHasLimitedSlots && (
                            <span
                              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                selectedIsSoldOut ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                              }`}
                            >
                              {selectedIsSoldOut ? 'Slot Full' : `Slots left: ${selectedAvailableSlots}`}
                            </span>
                          )}
                        </div>
                      )}
                      {!!prefilledValues[field.label] && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            Pre-filled
                          </span>
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
            {error && (
              <div className="mt-1.5 flex items-center text-red-500 text-xs">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                <p>{error.message || 'Required'}</p>
              </div>
            )}
          </div>
        );
      }

      case 'file':
        const isUploading = uploadingFiles[fieldName];
        const isUploaded = uploadedDocuments[fieldName];
        const hasPreview = filePreviews[fieldName];
        const hasFileError = !!error || !!fileErrors[fieldName];
        
        const fileFieldBg = hasFormBackground ? 'bg-white/70' : 'bg-white/50';
        return (
          <div key={fieldKey} className={`relative ${fileFieldBg} backdrop-blur-sm rounded-lg p-4 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 md:col-span-2`}>
            <Label htmlFor={fieldName} className="text-gray-700 font-medium flex items-center text-sm mb-2">
              <span>{field.label}</span>
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            
            {!isUploaded ? (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    id={fieldName}
                    type="file"
                    accept={field.accept}
                    onChange={(e) => handleFileChange(fieldName, e)}
                    className={`h-10 text-sm bg-white rounded-lg ${hasFileError ? 'border-red-500' : 'border-gray-200'} transition-all duration-200 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`}
                    disabled={isUploading}
                  />
                  {isUploading && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <div className="flex items-center space-x-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent"></div>
                        <span className="text-xs font-medium">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {field.accept?.split(',').join(', ')}
                </p>
              </div>
            ) : (
              <div className="bg-green-50/80 backdrop-blur-sm rounded-lg p-3 border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-xs font-medium text-green-800">Uploaded</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUploadedFile(fieldName)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                {hasPreview && (
                  <div className="mt-2 bg-white p-1.5 rounded-lg border border-green-200">
                    <img 
                      src={hasPreview} 
                      alt="Preview" 
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>
            )}
            
            {hasFileError && (
              <div className="mt-1.5 flex items-center text-red-500 text-xs">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                <p>Required</p>
              </div>
            )}
          </div>
        );

      case 'list': {
        const items = listValues[fieldName] || [''];
        const listFieldBg = hasFormBackground ? 'bg-white/70' : 'bg-white/50';

        const updateListItems = (newItems) => {
          setListValues(prev => ({ ...prev, [fieldName]: newItems }));
        };

        return (
          <div key={fieldKey} className={`relative ${listFieldBg} backdrop-blur-sm rounded-lg p-4 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 md:col-span-2`}>
            <Label htmlFor={fieldName} className="text-gray-700 font-medium flex items-center text-sm mb-2">
              <span>{field.label}</span>
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>

            <div className="space-y-2">
              {items.map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{itemIndex + 1}.</span>
                  <Input
                    value={item}
                    onChange={(e) => {
                      const updated = [...items];
                      updated[itemIndex] = e.target.value;
                      updateListItems(updated);
                    }}
                    placeholder={field.placeholder || 'Enter item'}
                    className={`h-10 text-base bg-white flex-1 rounded-lg ${error ? 'border-red-500' : 'border-gray-200'} transition-all duration-200`}
                  />
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = items.filter((_, i) => i !== itemIndex);
                        updateListItems(updated);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-10 w-10 p-0 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => updateListItems([...items, ''])}
                className="w-full border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all duration-200"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add More
              </Button>

            </div>

            {error && (
              <div className="mt-1.5 flex items-center text-red-500 text-xs">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                <p>At least one item is required</p>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const handleFaceCapture = (imageSrc) => {
    setFaceImage(imageSrc);
    setFaceError(null);
    setShowFaceCapture(false); // Hide the capture UI after successful capture
    showSuccess('Face photo captured successfully! Click Register to complete registration.');
  };

  const handleFaceError = (error) => {
    setFaceError(error.message || 'Failed to capture face image');
    setFaceImage(null);
  };

  const onSubmitForm = (data) => {
    const visibleFieldsForValidation = getVisibleFields(formSchema, data, prefilledValues);

    // File inputs are not registered with react-hook-form, so we validate them
    // explicitly here. This also covers file sub-fields exposed via dropdown options.
    const missingFileFields = {};
    for (const field of visibleFieldsForValidation) {
      if (field.type === 'file' && field.required) {
        const fName = toFieldName(field.label);
        if (!uploadedDocuments[fName]) {
          missingFileFields[fName] = true;
        }
      }
    }
    if (Object.keys(missingFileFields).length > 0) {
      setFileErrors(missingFileFields);
      const firstMissing = visibleFieldsForValidation.find(
        (f) => f.type === 'file' && f.required && missingFileFields[toFieldName(f.label)]
      );
      showError(`"${firstMissing?.label}" is required.`);
      return;
    }
    setFileErrors({});

    for (const field of visibleFieldsForValidation) {
      if (field.type === 'list' && field.required) {
        const items = listValues[toFieldName(field.label)] || [];
        const filledItems = items.filter(item => item && item.trim() !== '');
        if (filledItems.length === 0) {
          showError(`"${field.label}" requires at least one item.`);
          return;
        }
      }
    }

    const formData = {};
    const documentIds = [];
    
    const visibleFields = getVisibleFields(formSchema, data, prefilledValues);
    visibleFields.forEach(field => {
      const fieldName = toFieldName(field.label);
      
      if (field.type === 'file') {
        if (uploadedDocuments[fieldName]) {
          documentIds.push(uploadedDocuments[fieldName]);
          formData[field.label] = uploadedDocuments[fieldName];
        }
      } else if (field.type === 'list') {
        const items = listValues[fieldName] || [];
        const filledItems = items.filter(item => item && item.trim() !== '');
        formData[field.label] = filledItems;
      } else {
        formData[field.label] = data[fieldName];
      }
    });

    if (showPricing && validatePayment) {
      const full = chargeable(calculatePriceTotals(formSchema, data, prefilledValues, listValues), eventData?.currency || 'INR');
      const payable = priceRule ? applyDiscount(priceRule, full).finalAmount
        : couponQuote ? Number(couponQuote.finalAmount) : full;
      const paymentError = payable > 0 ? validatePayment() : null;
      if (paymentError) {
        showError(paymentError);
        return;
      }
    }

    // Pass form data, document IDs, userId, and face image to the parent
    const ruleQuote = priceRule
      ? applyDiscount(priceRule, chargeable(calculatePriceTotals(formSchema, data, prefilledValues, listValues), eventData?.currency || 'INR'))
      : null;
    onSubmit(formData, documentIds, uploadedUserId, faceImage, ruleQuote || couponQuote);
  };

  if (formSchema.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <p className="text-muted-foreground text-center">No form fields configured</p>
        </CardContent>
      </Card>
    );
  }

  const formWrapperBg = hasFormBackground ? 'bg-transparent' : 'bg-white/80 backdrop-blur-sm';
  const visibleFields = getVisibleFields(formSchema, watchedValues, prefilledValues);
  const priceTotals = calculatePriceTotals(formSchema, watchedValues, prefilledValues, listValues);
  const chargeCurrency = eventData?.currency || 'INR';
  const totalPrice = chargeable(priceTotals, chargeCurrency);
  const otherCurrencies = Object.entries(priceTotals).filter(([code, amount]) => code !== chargeCurrency && amount > 0);
  const ruleQuote = priceRule ? applyDiscount(priceRule, totalPrice) : null;
  const payableAmount = ruleQuote ? ruleQuote.finalAmount : couponQuote ? Number(couponQuote.finalAmount) : totalPrice;
  return (
    <div className={`${formWrapperBg} rounded-2xl shadow-xl border border-gray-100 overflow-hidden ${className}`}>
      <div className="p-5 sm:p-6">
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleFields.map((field, index) => renderField(field, index))}
          </div>
          
          {/* Pricing Summary */}
          {showPricing && (totalPrice > 0 || otherCurrencies.length > 0) && (
            <Card className="border-green-200 bg-green-50 md:col-span-2">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm text-green-800">Pricing Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-3">
                {ruleQuote && ruleQuote.discountAmount > 0 && (
                  <div className="flex justify-between items-center text-green-800 text-sm">
                    <span>{priceRule.label || 'Access code discount'}</span>
                    <span>− {formatCurrency(ruleQuote.discountAmount, chargeCurrency)}</span>
                  </div>
                )}
                {otherCurrencies.map(([code, amount]) => (
                  <div key={code} className="flex justify-between items-center text-green-800 text-sm">
                    <span>Also selected ({code})</span>
                    <span>{formatCurrency(amount, code)}</span>
                  </div>
                ))}
                <div className="border-t border-green-300 pt-1.5 mt-1">
                  <div className="flex justify-between items-center font-bold text-green-900 text-sm">
                    <span>Total Amount:</span>
                    <span>{formatCurrency(payableAmount, chargeCurrency)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {eventData?.enableCouponCode && showPricing && totalPrice > 0 && !priceRule && (
            <CouponApplyField
              eventId={eventId}
              email={
                watchedValues[toFieldName('Email')]
                || watchedValues.email
                || Object.entries(watchedValues).find(([k]) => k.toLowerCase().includes('email'))?.[1]
              }
              formData={Object.fromEntries(
                visibleFields.map((field) => [
                  field.label,
                  watchedValues[toFieldName(field.label)] ?? prefilledValues[field.label],
                ])
              )}
              currency="INR"
              quote={couponQuote}
              onApplied={setCouponQuote}
              onRemoved={() => setCouponQuote(null)}
            />
          )}

          {/* Face Registration Section */}
          {eventData?.faceIdEnabled && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg overflow-hidden border border-blue-100 shadow-sm md:col-span-2">
            <div className="p-4 border-b border-blue-100 bg-white/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Camera className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Face Registration</h3>
                    <p className="text-xs text-gray-600">Optional but recommended</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFaceCapture(prev => !prev)}
                  className="bg-white hover:bg-blue-50 border-blue-200 text-blue-600 hover:text-blue-700 h-8 text-xs"
                >
                  {showFaceCapture ? 'Cancel' : 'Register Face'}
                </Button>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                <div className="bg-blue-50/50 backdrop-blur-sm border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      Register your face for contactless check-in at the event.
                    </p>
                  </div>
                </div>
                
                {showFaceCapture && (
                  <>
                    <FaceCapture
                      onCapture={handleFaceCapture}
                      onError={handleFaceError}
                    />
                    {faceError && (
                      <div className="text-xs text-red-600">
                        {faceError}
                      </div>
                    )}
                  </>
                )}

                {faceImage && !showFaceCapture && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-700">Face ID registered!</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowFaceCapture(true);
                          setFaceImage(null);
                        }}
                        className="text-green-700 hover:text-green-800 hover:bg-green-100 h-7 text-xs px-2"
                      >
                        Retake
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>)}
          
          {showPricing && renderPayment && payableAmount > 0 && renderPayment(payableAmount)}

          <div className="pt-4 border-t border-gray-100 md:col-span-2">
            <Button 
              type="submit" 
              className="w-full h-11 text-base font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg" 
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DynamicForm;
